import { sortBy } from 'lodash';

import { Drive, FileItem } from '@mnspro-cloud/services';

/**
 * Gets the name of the given file without file extension.
 */
export const getBaseFilename = (filename: string): string => {
  const parts = filename.split('.');
  if (parts.length === 1) {
    return parts[0];
  }

  parts.pop();
  return parts.join('.');
};

/**
 * Extracts the file extension of the given filename.
 */
export const getFileextension = (filename: string): string => {
  const parts = filename.split('.');
  return parts[parts.length - 1];
};

export const officeExtensions = ['docx', 'doc', 'ppt', 'pptx', 'xls', 'xlsx', 'odt'];

export const isOfficeFile = (filename?: string | null): boolean => {
  if (!filename) {
    return false;
  }
  const extension = getFileextension(filename);

  return officeExtensions.includes(extension);
};

/**
 * Image formats every browser we target can render in an `<img>` tag.
 *
 * HEIC/HEIF is deliberately missing: it is the iPhone default and therefore common in
 * OneDrive, but only Safari can display it. TIFF (Safari only), JPEG XL and camera RAW
 * are left out for the same reason - those files keep downloading on click instead of
 * opening a preview that would stay empty.
 */
export const previewableImageMimeTypes = [
  'image/png',
  'image/jpeg',
  'image/gif',
  'image/webp',
  'image/bmp',
  'image/avif',
  'image/svg+xml',
];

export const previewableImageExtensions = [
  'png',
  'jpg',
  'jpeg',
  'gif',
  'webp',
  'bmp',
  'avif',
  'svg',
];

/**
 * Checks whether a MIME type and file name describe an image the preview can show.
 * Prefers the MIME type and falls back to the extension. Use this where files are
 * not Graph `FileItem`s, such as the parental communication file list.
 */
export const isPreviewableImageType = (
  mimeType?: string | null,
  filename?: string | null,
): boolean => {
  if (mimeType && previewableImageMimeTypes.includes(mimeType.toLowerCase())) {
    return true;
  }

  // Uploads sometimes carry a generic "application/octet-stream", so the extension
  // still gets a say. A mismatch only costs the error fallback in the overlay.
  if (!filename) {
    return false;
  }

  return previewableImageExtensions.includes(getFileextension(filename).toLowerCase());
};

/**
 * Checks whether a drive item can be shown in the image preview.
 */
export const isPreviewableImage = (file?: FileItem | null): boolean => {
  if (!file || file.folder) {
    return false;
  }

  return isPreviewableImageType(file.file?.mimeType, file.name);
};

/**
 * Characters that are not allowed in OneDrive/SharePoint file and folder names.
 * Emojis, '.', '!' and "'" are intentionally NOT included - they are valid.
 * @see https://support.microsoft.com/en-us/office/restrictions-and-limitations-in-onedrive-and-sharepoint-64883a5d-228e-48f5-b3d2-eb39e07630fa
 */
export const invalidFilenameChars = ['"', '*', ':', '<', '>', '?', '/', '\\', '|'];

/**
 * Names that are reserved by OneDrive/SharePoint and cannot be used for
 * files or folders (compared case-insensitively).
 */
export const reservedFilenames = [
  '.lock',
  'CON',
  'PRN',
  'AUX',
  'NUL',
  'desktop.ini',
  ...Array.from({ length: 10 }, (_, i) => `COM${i}`),
  ...Array.from({ length: 10 }, (_, i) => `LPT${i}`),
];

/**
 * Checks a file or folder name against the OneDrive/SharePoint naming rules.
 * Applies the same rules both when creating and when renaming items, so a
 * name accepted on creation is also accepted on rename.
 *
 * Leading/trailing whitespace is NOT rejected here - callers trim the name
 * before saving (OneDrive/SharePoint forbids edge whitespace, so trimming is
 * lossless), which is friendlier than blocking the user with an error.
 *
 * Returns true if the name is invalid because it:
 * - is empty (or whitespace-only),
 * - contains an invalid character ({@link invalidFilenameChars}),
 * - contains "_vti_" or starts with "~$",
 * - matches a reserved name ({@link reservedFilenames}).
 */
export const containsIllegalChars = (filename: string): boolean => {
  // Empty or whitespace-only names are not allowed.
  if (filename.trim().length === 0) {
    return true;
  }

  if (invalidFilenameChars.some((char) => filename.includes(char))) {
    return true;
  }

  // "_vti_" must not appear anywhere in the name.
  if (filename.toLowerCase().includes('_vti_')) {
    return true;
  }

  // Names starting with "~$" are reserved.
  if (filename.startsWith('~$')) {
    return true;
  }

  // Reserved names (compared case-insensitively).
  if (reservedFilenames.some((reserved) => reserved.toLowerCase() === filename.toLowerCase())) {
    return true;
  }

  return false;
};

/**
 * Filters a given list of files to only include folders and
 * files that have a file extension listed in "allowedExtensions".
 */
export const filterFileListForAllowedExtensions = (
  fileItems: FileItem[],
  allowedExtensions: string[],
) => {
  const filteredFiles = fileItems.filter((fileItem) => {
    if (fileItem.folder) {
      return true;
    }

    if (!fileItem.name) {
      return false;
    }

    const currentFileExtension = getFileextension(fileItem.name);

    if (allowedExtensions.includes(currentFileExtension)) {
      return true;
    }

    return false;
  });

  return filteredFiles;
};

/**
 * Sorts a list of drives for display in the drive list:
 * 1. "root" drive comes first
 * 2. Drives without label come next
 * 3. All others are sorted alphabetically (case-insensitive)
 */
export const sortDrivesForDisplay = (drives: Drive[]): Drive[] => {
  return sortBy(drives, [
    // 1. "root" kommt zuerst (niedrigste Priorität = 0)
    (drive) => (drive.label?.toLowerCase() === 'root' ? 0 : 1),
    // 2. Dann Items ohne Titel (Priorität = 0 wenn kein Titel)
    (drive) => (drive.label ? 1 : 0),
    // 3. Dann alphabetisch nach Titel
    (drive) => drive.label?.toLowerCase() ?? '',
  ]);
};
